import Text from '@components/Text';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import {
    Fragment, ReactNode, useState, MouseEvent,
} from 'react';

import TooltipCloseIcon from './TooltipCloseIcon';

export interface Props {
    children?: ReactNode;
    className?:string;
    top?: number;
    bottom?: number;
    right?: number | string;
    left?: number| string;
    visibleKey: string;
    align?: 'left' | 'right' | 'center';
    shape?: 'fill' | 'line';
    backgroundColor?: string;
    color?: string;
    hideClose?: boolean;
}

function Tooltip({
    children, className, top = 16, right = 20, bottom, left,
    visibleKey, align = 'center', shape = 'fill', backgroundColor, color, hideClose,
}:Props) {
    const fullKey = `tooltip-${visibleKey}`;
    const [isHidden, setIsHidden] = useState<boolean>(typeof window !== 'undefined' && !!localStorage.getItem(fullKey));
    const handleHide = (e: MouseEvent<HTMLOrSVGElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (typeof window !== 'undefined') {
            window.localStorage.setItem(fullKey, 'true');
        }
        setIsHidden(true);
    };
    if (isHidden) {
        return (<Fragment/>);
    }
    const isLine = shape === 'line';
    return (
        <Wrapper className={className}
            style={{
                top, right, bottom, left,
            }} >
            <Container>
                <TriangleWrapper align={align}>
                    <Triangle isLine={isLine}
                        style={{ backgroundColor }} />
                </TriangleWrapper>
                <Contents isLine={isLine}
                    style={{ backgroundColor }} >
                    <Text fontWeight='bold'
                        size='s'
                        css={textCss(isLine)}
                        color={color} >
                        {children}
                    </Text>
                    {(!hideClose) && (
                        <TooltipCloseIcon css={closeCss}
                            color={color}
                            onClick={handleHide} />
                    )}
                </Contents>
                {isLine && (
                    <Fix align={align}/>
                )}
            </Container>
        </Wrapper>
    );
}

function TooltipWrapper({ children }:{children?:ReactNode}) {
    return (
        <RelativeWrapper>
            {children}
        </RelativeWrapper>
    );
}

const triangle = (size: number, radius: boolean) => css`
    overflow: hidden;
    transform: translateY(40%) rotate(30deg) skewY(30deg) scaleX(.866);
    pointer-events: none;
    height: ${size}px;
    width: ${size}px;
    ${radius && 'border-radius: 20%;'}

    &::before,
    &::after {
        content: '';
        height: ${size}px;
        pointer-events: auto;
        position: absolute;
        width: ${size}px;
    }

    &::before {
        transform: scaleX(1.155) skewY(-30deg) rotate(-30deg) translateY(-42.3%) skewX(30deg) scaleY(.866) translateX(-24%);
        ${radius && 'border-radius: 20% 20% 20% 55%;'}
    }

    &::after {
        transform: scaleX(1.155) skewY(-30deg) rotate(-30deg) translateY(-42.3%) skewX(-30deg) scaleY(.866) translateX(24%);
        ${radius && 'border-radius: 20% 20% 55%;'}
    }
`;

const Wrapper = styled.div`
    position: absolute;
    z-index: 2;
`;

const RelativeWrapper = styled.div`
    position: relative;
`;

const Container = styled.div`
    align-items: center;
    display: flex;
    justify-content: center;
    position: relative;
    white-space: nowrap;
`;

const Contents = styled.div<{
    isLine: boolean;
}>`
    border-radius: 4px;
    display: flex;
    max-width: 80vw;
    padding: 7px 9px;
    position: relative;
    column-gap: 4px;
    align-items: center;
    ${({ isLine }) => (isLine ? css`
        background-color: var(--color-white);
        border: 1px solid var(--color-gray9);
    ` : css`
        background-color: var(--color-primary);
        border: 1px solid transparent;
    `)}
`;

const textCss = (isLine: boolean) => css`
    word-break: break-word;
    white-space: pre;
    color: ${isLine ? 'var(--color-gray9)' : 'var(--tooltip-text-color)'};
`;

const Triangle = styled.div<{
    isLine: boolean;
}>`
    ${triangle(20, true)}
    border: 1px solid;
    ${({ isLine }) => (isLine ? css`
        background-color: var(--color-white);
        border-color: var(--color-gray9);
    ` : css`
        background-color: var(--color-primary);
        border-color: transparent;
    `)}
`;

const TriangleWrapper = styled.div<{
    align: 'left' | 'right' | 'center';
}>`
    bottom: -16px;
    position: absolute;
    transform: rotate(180deg);
    ${({ align }) => align === 'right' && 'right: 16px;'}
    ${({ align }) => align === 'left' && 'left: 16px;'}
`;

const Fix = styled.div<{
    align: 'left' | 'right' | 'center';
}>`
    background-color: var(--color-white);
    border-radius: 1px;
    bottom: -1px;
    height: 3px;
    position: absolute;
    width: 14px;
    ${({ align }) => align === 'left' && 'left: 19px;'}
    ${({ align }) => align === 'right' && 'right: 19px;'}
`;

const closeCss = css`
    cursor: pointer;
`;

Tooltip.Wrapper = TooltipWrapper;

export default Tooltip;
